import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CameraIcon, UserIcon } from '@/shared/ui/icons';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiMeAvatarPost } from '@/entities/auth';
import type { ApiAuthMeResponse } from '@/shared/types/api/auth';
import { AVATAR_ACCEPTED_TYPES, AVATAR_MAX_BYTES } from '@/features/profile/modules/types';

type Props = {
  avatarUrl: string | null;
};

export const FeaturesProfileAvatarPicker = ({ avatarUrl }: Props) => {
  const { t } = useTranslation('profile');
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutate({
    api: (file: File) => apiMeAvatarPost(file),
    onSuccess: (data: ApiAuthMeResponse) => {
      queryClient.setQueryData(queryKeys.auth.me, data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      toast.success(t('toast.avatarUpdated'));
    },
    onError: (_err, message) => toast.error(message),
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      toast.error(t('errors.avatarType'));
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error(t('errors.avatarSize'));
      return;
    }
    mutation.mutate(file);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <span className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-faint">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <UserIcon className="h-12 w-12" aria-hidden />
          )}
          {mutation.isPending && (
            <span className="absolute inset-0 flex items-center justify-center bg-white/70">
              <Spinner size="md" />
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={mutation.isPending}
          aria-label={t('changePhoto')}
          className="absolute -bottom-1 -right-1 inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-brand-600 text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <CameraIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={mutation.isPending}
        className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-700 disabled:opacity-60"
      >
        {t('changePhoto')}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={AVATAR_ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
};
